import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Pane, ZoomControl, useMap } from 'react-leaflet';
import type { TileLayer as LeafletTileLayer } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useQuery } from '@tanstack/react-query';
import type { LatLngBoundsExpression } from 'leaflet';
import { queries } from '@/data';
import { useIsMobile } from '@/lib/useMediaQuery';
import { useViewport } from './useViewport';
import { useAdaptiveOpacity } from './useAdaptiveOpacity';
import { useMapStore } from './store';
import { useChoroplethStore } from '@/features/choropleth/store';

// Запас вокруг bbox страны, чтобы побережье и острова не упирались в барьер.
const BOUNDS_PADDING_DEG = 2;

interface MapInternalsProps {
  baseTileRef: React.RefObject<LeafletTileLayer | null>;
}

// Hillshade поверх подложки — даёт объёмные тени гор/долин.
// ESRI World_Hillshade: бесплатный, без ключа.
function TerrainLayer() {
  const visible = useChoroplethStore((s) => s.terrainVisible);
  if (!visible) return null;
  return (
    <TileLayer
      url="https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}"
      attribution="Hillshade &copy; Esri"
      opacity={0.55}
      className="terrain-tile"
      maxNativeZoom={13}
      maxZoom={18}
    />
  );
}

// Must be inside MapContainer to use react-leaflet hooks
function MapInternals({ baseTileRef }: MapInternalsProps) {
  useViewport();
  useAdaptiveOpacity(baseTileRef);
  const map = useMap();
  // Tooltip pane по умолчанию zIndex 650 — конфликтует с labelsPane (тоже 650).
  // Default-пейн уже создан Leaflet → пересоздавать через <Pane> нельзя (crash),
  // поднимаем zIndex императивно после mount.
  useEffect(() => {
    const pane = map.getPane('tooltipPane');
    if (pane) pane.style.zIndex = '800';
  }, [map]);

  // При начале панорамирования/зума — закрыть все открытые tooltips.
  // Иначе sticky-tooltip залипает если mouseout не сработал во время drag.
  useEffect(() => {
    type WithCloseTooltip = { closeTooltip?: () => void };
    const closeAllTooltips = () => {
      map.eachLayer((layer) => {
        const l = layer as unknown as WithCloseTooltip;
        if (typeof l.closeTooltip === 'function') l.closeTooltip();
      });
    };
    map.on('dragstart', closeAllTooltips);
    map.on('zoomstart', closeAllTooltips);
    return () => {
      map.off('dragstart', closeAllTooltips);
      map.off('zoomstart', closeAllTooltips);
    };
  }, [map]);

  return null;
}

export function BaseMap({ children }: { children?: React.ReactNode }) {
  const baseTileRef = useRef<LeafletTileLayer | null>(null);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  // Initial viewport читается из store ОДИН раз — Leaflet MapContainer uncontrolled.
  // useUrlSync уже populated store на boot, поэтому здесь актуальные значения.
  // useState lazy init — снимок store на момент первого рендера.
  const [initial] = useState(() => useMapStore.getState());
  const overlayHidden = useChoroplethStore((s) => s.overlayHidden);

  // maxBounds из bbox страны — раньше не выставлялся вовсе, и карту можно было
  // утащить в океан. Небольшой запас, чтобы края страны не упирались в барьер.
  const { data: meta } = useQuery(queries.meta());
  const maxBounds: LatLngBoundsExpression | undefined = meta
    ? [
        [meta.bbox[1] - BOUNDS_PADDING_DEG, meta.bbox[0] - BOUNDS_PADDING_DEG],
        [meta.bbox[3] + BOUNDS_PADDING_DEG, meta.bbox[2] + BOUNDS_PADDING_DEG],
      ]
    : undefined;

  return (
    <div
      className="relative h-full w-full"
      role="region"
      aria-label={t('map.region.ariaLabel', {
        country: meta?.country_label ?? '',
        defaultValue:
          'Interactive map of {{country}}. Screen-reader alternative: use the Filters, Layer, and POI panels.',
      })}
    >
    <MapContainer
      center={initial.center}
      zoom={initial.zoom}
      maxBounds={maxBounds}
      maxBoundsViscosity={0.4}
      className="h-full w-full"
      zoomControl={false}
      // On mobile, render vector overlays (8000 comuni paths, POI, selection)
      // to a Canvas instead of SVG. Drops DOM node count from ~8k to 1 per layer
      // and eliminates the freeze on the provinces→comuni view switch at zoom 10.
      // Desktop keeps SVG so the hover hatch pattern (fill=url(#choropleth-hatch))
      // continues to work.
      preferCanvas={isMobile}
    >
      {!isMobile && <ZoomControl position="bottomright" />}
      {/* Base tiles:
          - маски активны (overlayHidden=false) → CARTO Voyager без подписей, подписи идут отдельным слоем поверх хороплета
          - маски убраны (overlayHidden=true)   → OSM standard (подписи уже в тайле, поверх ничего нет) */}
      <TileLayer
        key={overlayHidden ? 'osm' : 'carto'}
        ref={baseTileRef}
        url={
          overlayHidden
            ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png'
        }
        attribution={
          overlayHidden
            ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            : '&copy; OSM &copy; CARTO'
        }
        opacity={1}
        maxZoom={19}
      />

      {/* Hillshade — рельеф через ESRI, multiply blend поверх подложки.
          Включается тогглом в LayerSwitcher. */}
      <Pane name="terrainPane" style={{ zIndex: 350 }}>
        <TerrainLayer />
      </Pane>

      {/* Подписи поверх хороплета — только в режиме CARTO (когда маски активны).
          В OSM-режиме (overlayHidden=true) подписи уже в базовом тайле. */}
      <Pane name="labelsPane" style={{ zIndex: 650 }}>
        {!overlayHidden && (
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
            attribution=""
          />
        )}
      </Pane>

      {/* Резервные panes для будущих слоёв (F1.2 choropleth, F1.4 POI, F1.5 selection) */}
      <Pane name="alpinePane" style={{ zIndex: 400 }} />
      {/* POI dots/markers — поверх choropleth (overlayPane=400) и hillshade (350),
          но под labelsPane (650), чтобы названия городов не закрывались точками. */}
      <Pane name="poiPane" style={{ zIndex: 620 }} />
      <Pane name="fitOverlayPane" style={{ zIndex: 690 }} />
      {/* selectionPane — saved-region highlight. Must sit above labels (650)
          and POI (620) but BELOW popupPane (default 700), otherwise the
          yellow fill bleeds over open Popups (e.g. idealista pin details). */}
      <Pane name="selectionPane" style={{ zIndex: 680 }} />

      <MapInternals baseTileRef={baseTileRef} />
      {children}
    </MapContainer>
    </div>
  );
}
